import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { Season } from '@wbk/api';
import { Button, ImageWithPlaceholder, ShareModal } from '@wbk/ui';
import { QueueView } from '@wbk/queue';
import { getEventStatus } from '@/util/events';
import BuyTicketButton from '../buttons/BuyButton';
import Reveal from '../animation/Reveal';

type Props = {
  season: Season;
  status: ReturnType<typeof getEventStatus>;
};

const MatchDetailBanner = ({ season, status }: Props) => {
  const { t } = useTranslation();

  return (
    <section className='relative h-full w-full overflow-hidden before:absolute before:inset-0 before:z-[1] before:bg-black/70'>
      <ImageWithPlaceholder
        className={`absolute inset-0 h-full w-full bg-white object-cover ${season.mobile_poster ? 'hidden lg:block' : ''}`}
        src={season.poster}
        sizes='1441px'
        alt={season.title}
      />
      {season.mobile_poster && (
        <ImageWithPlaceholder
          className={`absolute inset-0 h-full w-full bg-white object-cover ${season.mobile_poster ? 'lg:hidden' : ''}`}
          src={season.mobile_poster}
          sizes='600px'
          alt={season.title}
        />
      )}

      <div className='container relative z-10 flex h-full min-h-[400px] flex-col items-start justify-between px-4 py-8'>
        <div className='flex w-full items-center justify-between'>
          {/* Back Button */}
          <Button
            as={Link}
            href='/'
            shape='text'
            theme='white'
            className='flex items-center gap-2 text-sm ltr:pl-1 rtl:pr-1'
          >
            <Image
              className='ltr:rotate-180'
              src='/icons/common/arrow-white.svg'
              width={22}
              height={22}
              alt={t('common:back')}
            />
            <span>{t('common:back')}</span>
          </Button>
          <ShareModal
            data={{
              title: season?.title,
              text: season?.title,
            }}
            poster={season.poster}
            className='z-10 hidden md:flex'
          />
        </div>

        <div className='flex w-full flex-col-reverse justify-between gap-8 pb-10 md:flex-row'>
          <Reveal
            direction='left'
            className='relative z-10 mx-auto w-full max-w-3xl grow space-y-6 text-center md:mx-0 md:text-start'
          >
            <h1>{season.title}</h1>
            {season.sub_title && <p>{season.sub_title} </p>}
            <div data-testid='buy-tickets-cta' className='mx-auto max-w-md md:mx-0'>
              <BuyTicketButton event={season} eventStatus={status} />
            </div>
            <QueueView />
            <ShareModal
              data={{
                title: season?.title,
                text: season?.title,
              }}
              poster={season.poster}
              className='z-10 mx-auto w-full max-w-md md:mx-0 md:hidden'
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default MatchDetailBanner;
